/* Dynamic Plot code copyright 2015 (c) Tuva Labs Inc. */
/*
 * barElement.js -- constructor for Dyn.BarElement() that creates a Bar Chart in the plot,
 * with a subclass for the 4 major variations of the Bar Chart.
 * Bar Charts have either horizontal or vertical bars.
 */
import { PlotElement } from "./plotElement";

export const wrapDynBarElement = function(Dyn) {

    // BarSums records the sums, means, and counts used to compute the bar lengths
    class BarSums {
        constructor() {
            this.casesMax = 0;
            this.casesInCategories = [];
            this.sumsMax = 0;
            this.sumsMin = 0;
            this.sumsOfCategories = [];
            this.meansMax = 0;
            this.meansMin = 0;
            this.meansOfCategories = [];
        }
        setCountsAndSums( categoryCounts, categorySums ) {
            // given an array of case counts and value attribute sums (by category)
            // compute all the statistics necessary determine bar lengths along the axis
            // note that if negative or misssing cases are excluded, these mins and maxes will reflect that
            Dyn.assert( categoryCounts.length >= 0 && categoryCounts.length === categorySums.length );
            this.casesMax = 0;
            this.sumsMax = 0;   // always >= 0 for bar display purposes
            this.sumsMin = 0;   // always <= 0 for bar display purposes
            this.meansMax = 0;  // always >= 0 for bar display purposes
            this.meansMin = 0;  // always <= 0 for bar display purposes
            this.casesInCategories = categoryCounts;
            this.sumsOfCategories = categorySums;
            this.meansOfCategories = [];
            for (let i=0, j=categoryCounts.length; i<j; i++) {
                this.meansOfCategories.push( categorySums[i] * Dyn.mathUtil.invertSafely(categoryCounts[i]) );
                // get max values
                this.casesMax = Math.max( this.casesMax, categoryCounts[i] );
                this.sumsMax  = Math.max( this.sumsMax, categorySums[i] );
                this.sumsMin  = Math.min( this.sumsMin, categorySums[i] );
                this.meansMax = Math.max( this.meansMax, this.meansOfCategories[i] );
                this.meansMin = Math.min( this.meansMin, this.meansOfCategories[i] );
            }
        }
    } // class BarSums

    // BarAxisOptions has flags to indicate what axis options should be turned on or off
    //      for this particular axis of this particular bar chart.
    class BarAxisOptions {
        constructor() {
            this.isFrequencyAxis = false;   // show Freq axis scale
            this.isPercentAxis = false;     // show Percent axis scale?
            this.hasValueAttribute = false; // axis has a numeric value attribute to sum or average
            this.hasDisabledAttributes = false; // axis has 1+ attributes, all should be disabled?
        }
    } // class BarAxisOptions

    /**
     * Bar elements by frequency or value, that can be stacked or unstacked, stretched or unstretched.
     *
     * Frequency bars: Bars lengths are by case frequency (bars made up of blocks of the same size).
     * Value bars:	Bar lengths are by case value of the value attribute.
     * Stacked bars: Bars stacked up for a total count (frequency) or total sum (value bar), or mean (value bar).
     * Stretched Bars: relative value or relative frequency stacked bar charts.
     *
     * Derived classes: FreqBarElement(), ValueBarElement(), BarOfSumsElement(), BarOfMeansElement(), StretchedBarElement().
     */
    class BarElement extends PlotElement {
        /**
         * @param [wantStackedBars] {boolean}
         */
        constructor( wantStackedBars ) {
            super(Dyn);
            this.initElement( {
                priority: Dyn.EPlotPriority.kPriorityShape,
                replaceType: Dyn.EPlotControl.kControlsShape,
                primaryType: Dyn.EPlotControl.kControlsBarChart,
                otherControls: [
                    Dyn.EPlotControl.kControlsCaseOrder ]
            }, Dyn );

            // Child classes set these in the Constructor, so we know what type of Bar Chart to create
            this.barsStacked = Boolean(wantStackedBars);
            this.barsStretched = false; // want all stacked bars to be the same total length, normalized by stack height.
            this.barsByFrequencyOnly = false; // disallow value bars
            this.multipleNumericAttributesOK = true; // allow bar to based on 2+ numeric value attributes?
            this.negativeCaseValuesOK = true; // if bar is based on attribute case values, do we allow negative data values?

            // current state of bar chart on X-axis, Y-Axis: class BarAxisOptions
            this.onX = {};
            this.onY = {};
            this.onLegend = {};
            // other state vars
            //this.needsDefaultTitle = true;
            this.barsHorizontal = false; // e.g. true if (Freq. attr on Y only) || (Value attr on X only)
            this.barsSectioned = false; // true if we are stacked and have color categories on legend
            this.numAttributesUsedX = 0;
            this.numAttributesUsedY = 0;
            this.sums = null; // class BarSums to calculate axis endpoints and bar lengths
            this.marginForSum = 0; // reserved space on axis for "Bar totals", in pixels

            this[ Dyn.EPlotControl.kControlsMultiCaseSections ] = true;  // if true one icon per group of value attribute, if false one icon per case.
        }

        toString() {
            return "{BarElement}";
        }

        save() {
            let savedData = super.save();
            if( this.saveBarOrientation() ) {
                savedData.barsHorizontal = this.barsHorizontal;
            }
            return savedData;
        }

        restore( savedData ) {
            super.restore( savedData );
            this.needsXYPrompt = false; // set up prompts once on construction, not on recalc or restore
            if( typeof savedData.barsHorizontal !== 'undefined' ) {
                this.barsHorizontal = savedData.barsHorizontal;
            }
        }

        /**
            * Find the axis to use as a value axis for value bars, or return null if we should use frequency bars.
            * Value bars: bar length proportional to numeric value on value axis (non-numeric cases missing, negative value bars go in negative direction)
            * Frequency bars: bar lengths are all the same (of arbitrary length of a pleasing dimension).
            * Updates:
            *  this.numAttributesUsedX
            *  this.numAttributesUsedY
            *  this.onX
            *  this.onY
            *  this.onLegend
            * @param subViewRef - plot sub-views from Dyn.PlotView.getSubviewRef()
            */
        calcAxisOptions ( subViewRef) {
            let xAxisView = subViewRef.xAxisView, yAxisView = subViewRef.yAxisView;

            // clear all old settings
            this.onX = new BarAxisOptions();
            this.onY = new BarAxisOptions();
            this.onLegend = new BarAxisOptions();

            if( this.barsByFrequencyOnly  ) {
                // set up a Bar Chart of Frequencies (i.e. count of non-missing cases in each category)
                Dyn.assert( this.barsStacked && !(this.barsStretched || this.barOfMeans) ); // always stacked, never stretched.
                this.onX.isFrequencyAxis = ((yAxisView.isCategoricalActiveOrInactive()) && (!xAxisView.isCategoricalActiveOrInactive()))
                    || this.barsHorizontal; // preserve horizontal bars from swap axis on frequency bar
                this.onY.isFrequencyAxis = (! this.onX.isFrequencyAxis) && (!yAxisView.isCategoricalActiveOrInactive());
                this.onX.hasDisabledAttributes = xAxisView.isNumericActiveOrInactive();
                this.onY.hasDisabledAttributes = yAxisView.isNumericActiveOrInactive();
                // suppress numeric attributes and get count of other (categorical) attributes.
                this.numAttributesUsedX = (xAxisView.isNumericActiveOrInactive()) ? 0 :
                    xAxisView.allAttributes.count();
                this.numAttributesUsedY = (yAxisView.isNumericActiveOrInactive()) ? 0 :
                    yAxisView.allAttributes.count();
            } else {
                // set up a value/sum/mean bar chart if possible, identify the one numeric attribute to use
                // or set up a stretched bar char, with our without a numeric (value) attribute.
                // for these chart types, we must disable a 2nd of 2 numeric axes, or 2nd of 2 categorical axes, to make a sensible plot.
                let valueAxis;
                if( xAxisView.isNumericActiveOrInactive() && yAxisView.isNumericActiveOrInactive()) {
                    // favor axis with the most numeric attributes, or Y axis, to be consistent with calcNumIconsPerCase()
                    valueAxis = ( yAxisView.allAttributes.count() >= xAxisView.allAttributes.count() ? yAxisView : xAxisView );
                } else {
                    // else find numeric axis if any
                    valueAxis = ( yAxisView.isNumericActiveOrInactive() ? yAxisView : ( xAxisView.isNumericActiveOrInactive() ? xAxisView : null ));
                }
                this.onX.hasValueAttribute = (valueAxis === xAxisView);
                this.onY.hasValueAttribute = (valueAxis === yAxisView);
                let nonValueAxis = this.getNonValueAxis( xAxisView, yAxisView ),
                    categoryByCategoryPlot = Boolean( xAxisView.isCategoricalActiveOrInactive() && yAxisView.isCategoricalActiveOrInactive()),
                    disable2ndCategoryAxis = Boolean(categoryByCategoryPlot && ! this.barsStretched),
                    disableNonValueAxis = Boolean( nonValueAxis && nonValueAxis.isNumericActiveOrInactive()),
                    maxAttributes = this.multipleNumericAttributesOK ? Dyn.plotUtil.kMaxAttributesOnAxis : 1;
                this.onX.hasDisabledAttributes = (nonValueAxis === xAxisView) ? disableNonValueAxis : false ;
                this.onY.hasDisabledAttributes = (nonValueAxis === yAxisView) ? disableNonValueAxis : disable2ndCategoryAxis ;
                this.numAttributesUsedX = (this.onX.hasDisabledAttributes ? 0 :
                    Math.min( xAxisView.allAttributes.count(), maxAttributes ));
                this.numAttributesUsedY = (this.onY.hasDisabledAttributes ? 0 :
                    Math.min( yAxisView.allAttributes.count(), maxAttributes ));
                if( this.barsStretched ) {
                    let categoryOnYPlot = Boolean( yAxisView.isCategoricalActiveOrInactive() && xAxisView.isEmptyOfAttributes());
                    this.onX.isPercentAxis = Boolean( categoryOnYPlot || this.onX.hasValueAttribute
                        || (this.barsHorizontal && !this.onY.hasValueAttribute)); // preserve horizontal bars from swap axis if possible
                    this.onY.isPercentAxis = Boolean( ! ( this.onX.isPercentAxis || categoryByCategoryPlot ));
                }
            }

            // Set bar orientation
            //      in general bars go vertical by default, unless that does not make sense for the attribute arrangement
            let valueAxis = this.getValueAxis( xAxisView, yAxisView ),
                frequencyAxis = this.getFrequencyAxis( xAxisView, yAxisView ),
                percentAxis = this.getPercentAxis( xAxisView, yAxisView ),
                nonValueAxis = this.getNonValueAxis( xAxisView, yAxisView );
            if( valueAxis ) {
                this.barsHorizontal = Boolean(valueAxis.isHorizontal); // horizontal bars if value axis is horizontal
            } else if ( frequencyAxis ) {
                this.barsHorizontal = Boolean(frequencyAxis.isHorizontal); // horizontal bars if frequency axis is horizontal
            } else if ( percentAxis ) {
                this.barsHorizontal = Boolean(percentAxis.isHorizontal); // horizontal bars if percent axis is horizontal
            } else {
                this.barsHorizontal = Boolean(yAxisView.isCategoricalActiveOrInactive() && ! xAxisView.isCategoricalActiveOrInactive()); // horizontal bars if categorical axis on Y only
            }

            // disable the color/legend attribute if it would make sections in the Bar of Means, because sections don't make sense for Means
            let coloringAttribute = this.plotShaper.dataSet.getSelectedAttribute(),
                nonValueAxisAttribute = nonValueAxis && nonValueAxis.getAttribute();
            this.onLegend.hasDisabledAttributes = Boolean( this.barOfMeans && coloringAttribute && (coloringAttribute !== nonValueAxisAttribute));

            // sanity check
            Dyn.assert( ! (this.onX.isFrequencyAxis && this.onY.isFrequencyAxis), "should only be one frequency axis, not two");
            Dyn.assert( ! (this.onX.isPercentAxis && this.onY.isPercentAxis), "should only be one percent axis, not two");
            Dyn.assert( ! (frequencyAxis && percentAxis), "can not have both frequency axis and percent axis at the same time");
            Dyn.assert( ! (frequencyAxis && valueAxis), "can not have both frequency axis and value axis at the same time");
            Dyn.assert( ! (valueAxis && percentAxis && valueAxis !== percentAxis ), "percent axis must match the value axis if both exist");
        }

        /**
         * If a value axis is allowed for this bar type, and there is a
         * numeric attribute on that axis, return the axis, otherwise null.
         */
        getValueAxis( xAxisView, yAxisView ) {
            return this.onX.hasValueAttribute ? xAxisView : (this.onY.hasValueAttribute ? yAxisView : null);
        }

        /** Return the non-value axis (cross-axis) if there is a value axis (e.g. X if Y is the value axis); otherwise null. */
        getNonValueAxis( xAxisView, yAxisView ) {
            return this.onX.hasValueAttribute ? yAxisView : (this.onY.hasValueAttribute ? xAxisView : null);
        }

        /** Return the axis that has a frequency scale, if any; otherwise null. */
        getFrequencyAxis( xAxisView, yAxisView ) {
            return this.onX.isFrequencyAxis ? xAxisView : (this.onY.isFrequencyAxis ? yAxisView : null);
        }

        /** Return the non-frequency axis if there is a frequency axis (e.g. X if Y is the frequency axis) ; otherwise null. */
        getNonFrequencyAxis( xAxisView, yAxisView ) {
            return this.onX.isFrequencyAxis ? yAxisView : (this.onY.isFrequencyAxis ? xAxisView : null);
        }

        /** Return the axis that has a frequency scale, if any; otherwise null. */
        getPercentAxis( xAxisView, yAxisView ) {
            return this.onX.isPercentAxis ? xAxisView : (this.onY.isPercentAxis ? yAxisView : null);
        }

        /**
         * Return the allowable number of case icons show per data case, normally 1.
         *
         * Here we return 2+ if there are multiple attributes on Y or X, and we can plot them
         * in a dotplot/scatterplot/line plot.
         */
        calcNumIconsPerCase( ioWhatChanged, subViewRef ) {
            // determine if any of the attribute are suppressed and therefore should be ignored
            // NOTE: updates this.numAttributesOnX, this.numAttributesOnY,etc.
            this.calcAxisOptions( subViewRef );

            // get number of icons per case, and set numAttributesUsedX, numAttributesUsedY
            // compare to calcNumIconsAllowOneMultiAttributeAxis()
            // max of number of icons used on any one axis, and no less than 1.
            return Math.max( 1, Math.max( this.numAttributesUsedX, this.numAttributesUsedY ) );
        }

        /**
         * Restore the plot element during a SwapAxis, to swap the bar from one axis to another
         * @param savedData
         */
        swapAxis() {
            let savedData = this.save();
            if( this.saveBarOrientation()) {
                savedData.barsHorizontal = !this.barsHorizontal; // swap orientation for bars with no value attribute to swap
            }
            return [ this.primaryType, this.primaryType, savedData]; // [oldControlType, newControlType, savedData]
        }

        /** Update the plot's axis model, removing any elements incompatible with this PlotElement.
         * Sets:
         *    this.barsStacked
         *    this.barsHorizontal
         */
        calcAxis( ioWhatChanged, subViewRef ) {
            var xAxisView = subViewRef.xAxisView, yAxisView = subViewRef.yAxisView,
                rightMargin = subViewRef.rightMargin, topMargin = subViewRef.topMargin;

            this.updatePlotTitleAndAxisStyle( ioWhatChanged, subViewRef );

            let xAxisOptions = {
                    numActiveAttributes: this.numAttributesUsedX,
                    wantFrequencyAxis: this.onX.isFrequencyAxis,
                    wantPercentAxis: this.onX.isPercentAxis,
                    isIncompleteAxisStyle: this.onX.isIncompleteAxisStyle
                },
                yAxisOptions = {
                    numActiveAttributes: this.numAttributesUsedY,
                    wantFrequencyAxis: this.onY.isFrequencyAxis,
                    wantPercentAxis: this.onY.isPercentAxis,
                    isIncompleteAxisStyle: this.onY.isIncompleteAxisStyle
                };
            this.barsIncomplete = this.onX.isIncompleteAxisStyle
                || this.onY.isIncompleteAxisStyle
                || this.onLegend.isIncompleteAxisStyle;
            // disable/gray-out the legend if the bars are also grayed out (or if calcAxisOptions() wants this for Bar of Means)
            this.onLegend.hasDisabledAttributes = this.onLegend.hasDisabledAttributes || this.barsIncomplete;

            // reserve extra space on axis for sum, only if Count element is showing "Bar Totals" (section sums)
            let countElementShowingBarTotals = this.plotShaper.findElement( Dyn.EPlotControl.kControlsSectionSum ),
                isStretchedTo100Percent = this.barsStretched && (this.onX.isPercentAxis || this.onY.isPercentAxis);
            if( countElementShowingBarTotals ) {
                // get the estimated text size used by Bar Totals, Category or Cell Count, Category or Cell Percent, etc.
                // which all takes up space in the plot or category cell.
                // Note that cell sizes are not actually calculated until after calcAxis()
                // and text sizes are not actually calculated until after calcPosition
                // See postCalcPositionUpdate() which compares estimates with actual.
                let cellMarginXY = this.plotShaper.getCellMargin();
                if( this.barsHorizontal ) {
                    xAxisOptions.marginForSum = this.marginForSum = cellMarginXY.x;
                } else {
                    yAxisOptions.marginForSum = this.marginForSum = cellMarginXY.y;
                }
            } else if( isStretchedTo100Percent ) {
                // add margin at top, to give room between bars and the title or legend
                this.marginForSum = 2 * Dyn.preferences.getFontSize();
            } else {
                this.marginForSum = 0;
            }

            // hide unused numeric axes, and set number of active attributes
            xAxisView.setPlotSpecificOptions( xAxisOptions );
            yAxisView.setPlotSpecificOptions( yAxisOptions );
            // NOTE: we set up custom axis range later, in calcPlotSpecificRange(), after calcMissingValueCases().
            if( xAxisView.isIncompleteAxisStyle ) Dyn.assert( xAxisView.isIncompleteAxis(), "axis is not the right type for being incomplete" );
            if( yAxisView.isIncompleteAxisStyle ) Dyn.assert( yAxisView.isIncompleteAxis(), "axis is not the right type for being incomplete" );
        }

        // Dyn.BarElement.prototype.postCalcPostionUpdate = function() {
        //     // Reserved for future use to correct margins and debugging
        //     let newMarginForCount = this.plotShaper.getCellMargin();
        //     if( this.marginForSum && (
        //         this.marginForSum.x !== newMarginForCount.x ||
        //         this.marginForSum.y !== newMarginForCount.y )) {
        //         // margins have changed,
        //         //console.log("BarElement margins have changed", this.marginForSum, newMarginForCount );
        //     }
        // };

        /**
         * Record which cases that have missing values for the given plot.
         */
        calcMissingValueCases( numCases, xAxisView, yAxisView ) {

            var thisElement = this,
                missingCases = this.plotShaper.dataSet.missingCases,
                plotExcludesNegativeCaseValues = !this.negativeCaseValuesOK,
                valueAxis = this.getValueAxis( xAxisView, yAxisView );
            const usePreFilterStats = true; // so that filtered-out categories are not marked as missing (they should be marked as filtered only)

            xAxisView.postCalcAxisUpdate(); // update axis type if changed during calcAxis()
            yAxisView.postCalcAxisUpdate();

            // for each case
            for( let caseIndex = 0; caseIndex < numCases; caseIndex++ ) {
                let isMissing =
                    (isMissingForAnyAttributesOnAxis( caseIndex, xAxisView, this.numAttributesUsedX )) ||
                    (isMissingForAnyAttributesOnAxis( caseIndex, yAxisView, this.numAttributesUsedY ));
                missingCases.setCaseIsMissing( caseIndex, isMissing );
            }

            function isMissingForAnyAttributesOnAxis( caseIndex, axisView, numAttributesUsedOnAxis ) {
                Dyn.assert( !axisView.needsCalcAxis );
                var isValid = true,
                    excludeNegativeValues = (axisView === valueAxis && plotExcludesNegativeCaseValues);
                for( let attrIndex = 0; attrIndex < numAttributesUsedOnAxis; attrIndex++ ) {
                    if( attributeValueIsMissing( caseIndex, attrIndex, axisView, excludeNegativeValues ) )
                        isValid = false;
                }
                return !isValid;
            }

            function attributeValueIsMissing( caseIndex, attributeIndex, axisView, excludeNegativeValues ) {
                var caseValue = axisView.getCaseDataAsNumber( caseIndex, attributeIndex, usePreFilterStats ),
                    isValid;
                if( excludeNegativeValues ) {
                    // exclude non-numeric and negative values on this axis
                    isValid = thisElement.isNumericNonNegative( caseValue );
                } else {
                    // exclude missing (blank) values for this attribute
                    isValid = isFinite( caseValue ); // exclude blank categorical value
                }
                return !isValid;
            }
        }

        /**
         * Adjust the axis endpoints, depending on the type of bar chart.
         * Value bars: bars start at zero, and end at the longest bar.
         * @param numCases
         * @param subViewRef
         */
        calcPlotSpecificRange( numCases, subViewRef ) {
            var valueAxis = this.getValueAxis( subViewRef.xAxisView, subViewRef.yAxisView ),
                nonValueAxis = this.getNonValueAxis( subViewRef.xAxisView, subViewRef.yAxisView ),
                frequencyAxis = this.getFrequencyAxis( subViewRef.xAxisView, subViewRef.yAxisView ),
                nonFrequencyAxis = this.getNonFrequencyAxis( subViewRef.xAxisView, subViewRef.yAxisView ),
                percentAxis = this.getPercentAxis( subViewRef.xAxisView, subViewRef.yAxisView ),
                plotRange;

            this.sums = new BarSums(); // reset sums/counts
            // if filters were changed or especially if missing value cases just changed
            //      update the attribute stats now, so that numeric sums and categories and number of cases are correct.
            subViewRef.xAxisView.postCalcAxisUpdate();
            subViewRef.yAxisView.postCalcAxisUpdate();

            if( percentAxis ) {
                // set up the 0-100% scale of a stretched, stacked axis (with or without a value attribute)
                if ( valueAxis ) {
                    // if stretched sums of an attribute, get the sums
                    this.sumStackedCaseValues(valueAxis, nonValueAxis, true);
                }   // else percents are of case frequencies in each cell, which we already have
                plotRange = new Dyn.mathUtil.Range( 0, 100 );
                percentAxis.setPlotSpecificOptions({
                    plotMinMax : plotRange,
                    marginForSum : this.marginForSum,
                    numActiveAttributes: percentAxis.isHorizontal ? this.numAttributesUsedX : this.numAttributesUsedY,
                    wantPercentAxis : true,
                    isIncompleteAxisStyle: (percentAxis.isHorizontal ? this.onX : this.onY).isIncompleteAxisStyle
                });
            } else if( frequencyAxis ) {
                // set up the visible frequency axis
                // NOTE: this call to Dyn.AxisView.setPlotSpecificOptions() is the 2nd call, after initial settings in calcAxis()
                // NOTE: this.plotShaper.bins.maxCasesInCells is not calculated yet, check it later.
                this.sumStackedCaseValues( frequencyAxis, nonFrequencyAxis, false );
                plotRange = new Dyn.mathUtil.Range( 0, this.sums.casesMax );
                frequencyAxis.setPlotSpecificOptions({
                    plotMinMax : plotRange,
                    marginForSum : this.marginForSum,
                    numActiveAttributes: frequencyAxis.isHorizontal ? this.numAttributesUsedX : this.numAttributesUsedY,
                    wantFrequencyAxis: true,
                    isIncompleteAxisStyle: (frequencyAxis.isHorizontal ? this.onX : this.onY).isIncompleteAxisStyle
                });
            } else if( valueAxis ) {
                // on the value axis, extend numeric range from zero to the tallest bar, so that all bars are visible
                // handle both negative and positive values, e.g. [0-5], [-5 to 5] or [-5 to 0], etc.
                Dyn.assert( !( percentAxis || frequencyAxis ));
                if ( this.barsStacked ) {
                    this.sumStackedCaseValues(valueAxis, nonValueAxis, true);
                    plotRange = this.barOfMeans ? new Dyn.mathUtil.Range( this.sums.meansMin, this.sums.meansMax )
                        : new Dyn.mathUtil.Range( this.sums.sumsMin, this.sums.sumsMax );
                } else {
                    plotRange = new Dyn.mathUtil.Range( valueAxis.allAttributes.stats.min, valueAxis.allAttributes.stats.max );
                    if ( plotRange.min > 0 ) {
                        plotRange.min = 0; // extend positive axis to zero so bars start at zero
                        Dyn.assert( plotRange.max > 0 );
                    } else if ( plotRange.max < 0 ) {
                        plotRange.max = 0; // extend negative axis to zero so bars start at zero
                        Dyn.assert( plotRange.min < 0 );
                    } else {
                        // else data range already includes zero
                        plotRange.makeNonZeroWidth(); // in case all values are missing or equal
                        Dyn.assert( plotRange.min <= 0 && plotRange.max >= 0 );
                    }
                }
                // NOTE: this call to Dyn.AxisView.setPlotSpecificOptions() is the 2nd call, after initial settings in calcAxis()
                valueAxis.setPlotSpecificOptions({
                    plotMinMax : plotRange,
                    marginForSum : this.marginForSum,
                    numActiveAttributes: valueAxis.isHorizontal ? this.numAttributesUsedX : this.numAttributesUsedY,
                    isIncompleteAxisStyle: (valueAxis.isHorizontal ? this.onX : this.onY).isIncompleteAxisStyle
                });
            }

            // set up Legend/RightMargin/Coloring-Attribute
            let coloringAttribute = this.plotShaper.dataSet.getSelectedAttribute(),
                useNumericIntervals = false;
            if( coloringAttribute ) {
                if( this.barsStacked ) {
                    // create or update numeric intervals, even if coloring attribute will be disabled
                    subViewRef.rightMargin.postCalcAxisUpdate(); // first update stats to latest, due to changes from calcMissingValueCases()
                    useNumericIntervals = coloringAttribute.setNumericIntervals( true );
                    if ( useNumericIntervals ) {
                        //console.log( "BarElement numIntervals adjusted to " + coloringAttribute.numericIntervals.numIntervals + " min=" + coloringAttribute.numericIntervals.min + " max=" + coloringAttribute.numericIntervals.max );
                    }
                } else {
                    coloringAttribute.setNumericIntervals( false ); // clear numeric intervals, if any.
                }
                if( this.onLegend.hasDisabledAttributes ) {
                    coloringAttribute = null; // coloring attribute disabled
                }
            }
            // set or clear flag so that views know plotShaper.hasElement( Dyn.EPlotControl.kControlsNumericIntervals ) is true or not
            this[ Dyn.EPlotControl.kControlsNumericIntervals ] = useNumericIntervals;

            // group cases into sections,
            //  if stacked with attribute in the legend (logic above creates categories for numerical attributes)
            //  or if stacked with means and value attribute (uncolored, only one section is allowed per bar)
            //  else allow each (uncolored) case to be selected individually
            this.barsSectioned = Boolean( this.barOfMeans ? valueAxis :( this.barsStacked && coloringAttribute ));
            // keep kControlsMultiCaseSections flag in sync, to get section or case tooltips and counts to work correctly
            this[ Dyn.EPlotControl.kControlsMultiCaseSections ] = this.barsSectioned;

            // display or clear intervals and +- on legend
            subViewRef.topMargin.setPlotSpecificOptions( null );
            subViewRef.rightMargin.setPlotSpecificOptions( {
                disableAttribute: this.onLegend.hasDisabledAttributes,
                useNumericIntervals: useNumericIntervals,
                showPlusMinus: useNumericIntervals,
                disablePlusButton: coloringAttribute?.numericIntervals?.isAtMax,
                disableMinusButton: coloringAttribute?.numericIntervals?.isAtMin,
                isIncompleteAxisStyle: this.onLegend.isIncompleteAxisStyle
            });
        }

        /**
            * For stacked value bars, sum the case values on the value axis.
            * For stacked frequency bars, sum the number of cases in each category
            * This excludes negative and non-numeric case values, which should be treated as
            * missing cases by isPlottableValueBar() (must keep these in sync)
            * Used to find the range needed for the frequency, sum, or mean axis
            * @param {Dyn.AxisView} valueOrFreqAxis
            * @param {Dyn.AxisView} crossAxis
            * @param {boolean} useCaseValues - if true, sum the numeric case values, otherwise just get case counts
            */
        sumStackedCaseValues( valueOrFreqAxis, crossAxis, useCaseValues ) {
            var numCategories = Math.max( 1, ( crossAxis.isCategoricalAxis() ? crossAxis.allAttributes.stats.numCategories() : 0 )), // default of 1 category
                dataSet = this.plotShaper.dataSet,
                numCases = dataSet.getNumCasesPreFilter(), // includes missing+filtered-out cases (note: don't rely on this.plotShaper.numCasesInDataSet, #1330)
                caseIndex, caseValue, attrIndex, categoryValue;

            // default to data array length 1, unless there are multiple categories
            var categorySums   = Dyn.dataUtil.filledArray( numCategories, 0 ),
                categoryCounts = Dyn.dataUtil.filledArray( numCategories, 0 );
            Dyn.assert( this.barsStacked ); // should not be called for non-stack Bar

            // for each non-excluded case
            if( useCaseValues ) {
                // sum of case values for each case/attribute-value pair
                Dyn.assert( this.isValueBarChart() && this.barsStacked );
                let valueAxisNumAttributes = valueOrFreqAxis.isHorizontal ? this.numAttributesUsedX : this.numAttributesUsedY;
                for( caseIndex = 0; caseIndex < numCases; caseIndex++ ) {
                    if( dataSet.isCaseNonMissingNonFiltered( caseIndex ) ) {
                        // for each of multiple attributes on value axis
                        for( attrIndex = 0; attrIndex < valueAxisNumAttributes; attrIndex++ ) {
                            caseValue = valueOrFreqAxis.getCaseDataAsNumber( caseIndex, attrIndex );
                            Dyn.assert( this.negativeCaseValuesOK ? (!Dyn.dataUtil.isMissing( caseValue )) : this.isNumericNonNegative( caseValue ) );
                            categoryValue = (numCategories > 1) ? crossAxis.getCaseDataAsNumber( caseIndex ) : 0;
                            if( isFinite( categoryValue ) ) {
                                categorySums[categoryValue] += caseValue;
                                categoryCounts[categoryValue]++;
                            }
                        }
                    }
                }
            } else {
                // sum===count of non-missing cases in each category
                Dyn.assert( this.isFrequencyBarChart() && this.barsStacked );
                for( caseIndex = 0; caseIndex < numCases; caseIndex++ ) {
                    if( dataSet.isCaseNonMissingNonFiltered( caseIndex ) ) {
                        // for each non-missing case
                        categoryValue = (numCategories > 1) ? crossAxis.getCaseDataAsNumber( caseIndex ) : 0;
                        if( isFinite( categoryValue ) ) {
                            categorySums[categoryValue]++;
                            categoryCounts[categoryValue]++;
                        }
                    }
                }
            }

            this.sums.setCountsAndSums( categoryCounts, categorySums );
        }

        /**
         * Change icon style to Rectangle (default is a circle)
         * @param ioPositions
         * @param xAxisView
         * @param yAxisView
         */
        calcIconStyle( ioPositions, xAxisView, yAxisView ) {
            var numCases = ioPositions.length; // bar width may be determined by number of cases
            this.plotShaper.iconStyler.setStyleRectangle( numCases );
        }

        /** update the case positional data (shared by all elements) and adornments (specific to this element) */
        calcPosition( ioPositions, xAxisView, yAxisView /*, ioAdornments*/ ) {
            var iconStyler = this.plotShaper.iconStyler,
                dataSet = this.plotShaper.dataSet,
                xIsSeparated = xAxisView.isCategoricalActiveAxis(),
                yIsSeparated = yAxisView.isCategoricalActiveAxis(),
                plotCells = this.plotShaper.bins.cells,
                numCells = plotCells.length,
                numCasesInDataSet = this.plotShaper.numCasesInDataSet,
                sortingAttribute = dataSet.getOrderByAttribute(),
                coloringAttribute = dataSet.getSelectedAttribute();

            if ( ! this.allowCustomOrderBy() ) {
                // for stack bars, sorting attribute should be the same as the coloring attribute,
                //      to correctly form the sections that match the distinct colors.
                // unstacked bars or bars with no sections, can be ordered in different ways.
                sortingAttribute = coloringAttribute;
            }

            // get case values, missing values, and bin locations {xBinIndex, yBinIndex, cellIndex}
            let i, j, p;
            for( i=0, j=ioPositions.length; i<j; i++ ) {
                p = ioPositions[i];
                p.caseIndex = i % numCasesInDataSet;
                p.xAttrIndex = ((this.numAttributesUsedX < 2) ? 0 : Dyn.mathUtil.floor( i / numCasesInDataSet ));
                p.yAttrIndex = ((this.numAttributesUsedY < 2) ? 0 : Dyn.mathUtil.floor( i / numCasesInDataSet ));
                p.x = xAxisView.getCaseDataAsNumber( p.caseIndex, p.xAttrIndex );
                p.y = yAxisView.getCaseDataAsNumber( p.caseIndex, p.yAttrIndex );
                p.isVisible = dataSet.isCaseNonMissingNonFiltered( p.caseIndex );
                this.calcBinIndex( p, xIsSeparated, yIsSeparated );
                // convert from data coordinates (numeric value, category index, etc) to view coordinates (pixel position)
                p.x = xAxisView.dataToPixel( p.x );
                p.y = yAxisView.dataToPixel( p.y );
                setPositionIfMissingOrFiltered( p ); // position all invisible cases offscreen
            }
            this.populateCellsWithCases( ioPositions );	// create cell data

            // position all visible cases as bars, one cell at a time
            let isStackedValueBar = this.isValueBarChart() && this.barsStacked,
                isStackedFrequencyBar = this.isFrequencyBarChart() && this.barsStacked,
                valueAxis = this.getValueAxis( xAxisView, yAxisView ),
                frequencyAxis = this.getFrequencyAxis( xAxisView, yAxisView ),
                percentAxis = this.getPercentAxis( xAxisView, yAxisView ),
                maxSum = 0;
            for( i=0; i<numCells; i++ ) {
                this.positionBarsInCell( ioPositions, plotCells[i], sortingAttribute, valueAxis, frequencyAxis, percentAxis, setPositionHiddenInPlace );
                maxSum = Math.max( maxSum, ( isStackedValueBar ? plotCells[i].barData.valueSum :
                    ( isStackedFrequencyBar ?  plotCells[i].barData.caseCount :
                        0 )));
            }
            Dyn.assert( Dyn.mathUtil.isClose( maxSum, this.sums.sumsMax, 1e-5 ), "max bar sum from calcPosition() does not match calcPlotSpecificRange()");

            /** set the position of hidden cases to offscreen (to be animated on screen when status changes) */
            function setPositionIfMissingOrFiltered( p ) {
                if( !(p.isVisible)) {
                    p.x = iconStyler.missingValuePosition.x;
                    p.y = iconStyler.missingValuePosition.y;
                    p.width = p.height = Dyn.plotUtil.missingCaseSize;
                }
            }
            /** set the status of 2+ case in a bar section as invisible but in the right place in bar, for later animation.  */
            function setPositionHiddenInPlace( p ) {
                p.isVisible = false; // mark icon as invisible (to optimize icons)
                p.width = p.height = Dyn.plotUtil.missingCaseSize; // change size to zero to hide in place
            }
        }

        positionBarsInCell( ioPositions, cell, sortingAttribute, valueAxis, frequencyAxis, percentAxis, setPositionHiddenInPlace ) {
            const st = new BarStyle( cell, this.barsHorizontal, this.barsStacked, this.plotShaper.bins.maxCasesInCells, this.marginForSum );
            let cellCategoryIndex = (valueAxis && valueAxis.isHorizontal) ? cell.yBinIndex : cell.xBinIndex, // categories map to bins on other axis
                barSum = 0,
                valuesReversed = Boolean( valueAxis && ! valueAxis.allAttributes.isSortAscending ),
                valuesSigned = Boolean( this.negativeCaseValuesOK && valueAxis ),
                stackDirection = valuesSigned ? 1 : (( this.barsHorizontal !== valuesReversed ) ? 1 : -1),
                oneIconPerSection = this[ Dyn.EPlotControl.kControlsMultiCaseSections];
            let isMean = Boolean( valueAxis && this.barOfMeans ),
                isSum = Boolean( valueAxis && this.barsStacked && !isMean ),
                isStretched = Boolean( percentAxis || this.barsStretched ),
                isValueBar = Boolean( valueAxis && ! (isMean || isSum || isStretched));

            // compute bar length (fixed length for frequency bars, variable for value bars)
            let barLength;
            if( this.barsStacked ) {
                if( frequencyAxis ) {
                    Dyn.assert(!(this.barsStretched || valuesReversed)); // we don't expect stretched or reversed axis to combine with frequency axis
                    barLength = frequencyAxis.coordMap.scaleAbs(1);
                } else if( this.barsByFrequencyOnly ) {
                    // frequency plot, with NO frequency axis scale (because there are category bins on both axes) :
                    Dyn.assert( ! frequencyAxis);
                    barLength = st.cellMaxLength * Dyn.mathUtil.invertSafely( st.maxCasesInCells );
                } else if( isStretched && ! valueAxis ) {
                    if( percentAxis ) {
                        // if stretched bar graph of frequencies, with percent axis style :
                        //  bar length adjusted so N cases stacked go from 0% to 100%
                        barLength = percentAxis.coordMap.scaleAbs( 100 * Dyn.mathUtil.invertSafely( st.numCasesInCell ) );
                    } else {
                        // if stretched bar graph of frequencies, with NO percent axis scale (because there are category bins on both axes) :
                        //  bar length adjusted so sum of case in cell covers entire cell (with some margin)
                        barLength = st.cellMaxLength * Dyn.mathUtil.invertSafely( st.numCasesInCell );
                    }
                } else {
                    // default bar length if stacking cases of equal length (e.g. if no value attribute for Bar of Values,Sums,Means)
                    barLength = st.cellMaxLength * st.kBarLengthStacked * Dyn.mathUtil.invertSafely( st.maxCasesInCells );
                }
            } else {
                barLength = st.cellMaxLength * st.kBarLengthUnstacked; // for Value Bars that don't yet have a value attribute (incomplete plot)
            }

            // get value normalizer to scale mean or stretched bars
            let valueNormalizer = 1; // caseValue * valueNormalizer to stretch bars if needed
            if( valueAxis ) {
                if( isMean ) {
                    // if mean bars, get multiplier so each of n cases in bar contributes 1/n to bar
                    Dyn.assertArrayIndex(this.sums.casesInCategories, cellCategoryIndex);
                    valueNormalizer = Dyn.mathUtil.invertSafely(this.sums.casesInCategories[cellCategoryIndex]);
                } else if( isStretched ) {
                    // if stretched bar graph of sums of a numeric value attribute, with percent axis style :
                    //  get multiplier to map case values to pixels so sum of N cases stacked go from 0% to 100%
                    Dyn.assert( valueAxis === percentAxis );
                    Dyn.assertArrayIndex( this.sums.sumsOfCategories, cellCategoryIndex );
                    valueNormalizer = 100 * Dyn.mathUtil.invertSafely( this.sums.sumsOfCategories[cellCategoryIndex] );
                } else if( isValueBar ){
                    cell.barValues = []; // array of class barValues for CountElement to use
                }
            }
            let barWidth = this.barsStacked ? st.cellMaxWidth : st.cellMaxWidth * Dyn.mathUtil.invertSafely( st.maxCasesInCells );
            let displayWidth = Math.max( st.kMinBarWidthPixels, // if bars very, very small, allow them to overlap to be visible
                barWidth * (( this.barsStacked || st.maxCasesInCells===1 ) ? st.kBarWidthSingle : st.kBarWidth ));
            let barWidthMargin = (barWidth - displayWidth)/ 2; // 1/2 of distance between bars

            if ( sortingAttribute ) {
                // NOTE: we sort in reverse order to get top-to-bottom ordering in vertical stacks of bars, to match legend order.
                let wantReverseSort = this.barsStacked ? sortingAttribute.isSortAscending : ! sortingAttribute.isSortAscending;
                this.sortCasesInCell( cell, sortingAttribute, ioPositions, wantReverseSort );
            }
            Dyn.PlotSection.createSections( cell, BarSection.paramName ); // create cell.barSections array

            // compute starting positions of bars in cell
            Dyn.assert((((valueAxis?1:0)+(frequencyAxis?1:0)+(percentAxis?1:0)) <= 1) ||
                (percentAxis === valueAxis && ! frequencyAxis), "only one kind of value axis allowed, unless percentAxis===valueAxis");
            st.getBarStartPoint( this.barsHorizontal, cell, st.numCasesWide, barWidth, barWidthMargin, valueAxis || frequencyAxis || percentAxis );
            let section, sectionValue, prevSectionValue=-1, pFirst;// used in for loop across iterations
            let displayLength = barLength;

            // set parameters for a bar {x,y,height,width}
            // 	either uniform-length, unstacked bars of arbitrary length (kBarLengthUnstacked)
            //	or variable-length, unstacked bars along numeric axis
            for ( let i = 0; i < st.numCasesInCell; i++ ) {
                let iconIndex = cell.cases[i], caseValue, valueAttribute;
                Dyn.assertArrayIndex( ioPositions, iconIndex );
                let p = ioPositions[iconIndex];
                if( valueAxis ) { // get the length of each variable-length bar
                    valueAttribute = valueAxis.allAttributes.attributes[ valueAxis.isHorizontal ? p.xAttrIndex : p.yAttrIndex ];
                    caseValue = valueAxis.getCaseDataAsNumber( p.caseIndex, valueAxis.isHorizontal ? p.xAttrIndex : p.yAttrIndex );
                    if( this.barsStacked ) {
                        barSum += caseValue; // NOTE: can be 0 or (or negative if bar of means (no sections)), see negativeCaseValuesOK and calcMissingValueCases()
                        barLength = valuesSigned ? valueAxis.coordMap.signedLength(caseValue * valueNormalizer)
                            : valueAxis.coordMap.scaleAbs(caseValue * valueNormalizer); // offset for stack bars, may vary from displayed bar length
                    } else {
                        barLength = valueAxis.coordMap.signedLength( caseValue ); // note: bar length can go in negative direction
                        //console.log( this.barsHorizontal?"H":"V", stackDirection, valuesReversed?"Reversed":"NotReversed", caseValue, barLength );
                    }
                    displayLength = ( Dyn.mathUtil.isClose( barLength, 0, st.kMinBarLengthPixels/2 ))
                        ? st.kMinBarLengthPixels  // adjust display length of very short bar to make it more visible (bars will overlap)
                        : barLength;
                }
                if( this.barsHorizontal ){
                    p.x = st.start.x;
                    p.y = st.start.y - ( this.barsStacked ? 0 : i * barWidth);
                    p.width = displayLength * stackDirection; // bar length on X
                    p.height = displayWidth; // bar width on Y
                    if( this.barsStacked ) {
                        st.start.x += barLength * stackDirection;
                    }
                } else {
                    p.x = st.start.x + ( this.barsStacked ? 0 : i * barWidth);
                    p.y = st.start.y;
                    p.width = displayWidth; // bar width on X
                    p.height = displayLength * stackDirection; // bar length on Y
                    if( this.barsStacked ) {
                        st.start.y += barLength * stackDirection;
                    }
                }
                Dyn.mathUtil.normalizeRect( p ); // handle bars in negative directly correctly
                let barRect = new Dyn.mathUtil.Rect( p.x, p.y, p.width, p.height )

                // set parameters for each bar section (1+ cases with same sort attribute value)
                if( this.barsSectioned ) {
                    if ( sortingAttribute && ! this.barOfMeans ) {
                        // if sorting, make a section for groups of equal values
                        sectionValue = sortingAttribute.cases[p.caseIndex];
                        if( sortingAttribute.hasNumericIntervals() ) {
                            sectionValue = sortingAttribute.getNumericIntervalString( p.caseIndex );
                        }
                    } else if( valueAxis ){
                        // if not sorting, make a section for each attribute on value axis
                        sectionValue = valueAxis.isHorizontal ? p.xAttrIndex : p.yAttrIndex;
                    }
                    if ( i === 0 || sectionValue !== prevSectionValue ) {
                        // with change in case value, start a new section (0th case always starts new section)
                        section = createSectionFromBar( p.x, p.y, sectionValue, sortingAttribute, isSum, isMean, valueAttribute );
                        cell.barSections.push( section );
                        pFirst = p; // save reference to first icon in section for later
                    }
                    updateSectionFromBar( section, barRect, caseValue, st.start, valueAttribute );
                    if( oneIconPerSection && p !== pFirst && pFirst ) {
                        // this icon is for the 2nd+ icon in the group, and we want one icon per group
                        Dyn.mathUtil.copyRect( pFirst, section.rect ); // expand first bar to cover this bar also
                        setPositionHiddenInPlace( p ); // hide icon in place
                    }
                    p.sectionIndex = cell.barSections.length - 1; // store section index with icon
                    prevSectionValue = sectionValue;
                } else if( isValueBar ) {
                    cell.barValues.push( new BarValue( caseValue, barRect ));
                }
            }

            // save cell.barData for count or sum displays
            cell.barData = new BarData( st.numCasesInCell, !(this.barsHorizontal), (valuesReversed && ! this.barsStretched));
            cell.barData.setType( this.barsStacked, isSum, isMean, isStretched, isValueBar );
            cell.barData.setValueAttributeID( valueAxis );
            if( this.barsStacked ) {
                cell.barData.setValueSum( barSum ); // sum can be sum of values, or sum of counts
                cell.barData.setBarEnd( this.barsHorizontal ? st.start.x : st.start.y ); // top X or Y of stack
            }
            //console.log( "barWidth: "+barWidth+ " barWidthMargin: "+barWidthMargin+" hiddenBorders: "+this.plotShaper.iconStyler.hideBorder );

            function createSectionFromBar( x, y, sectionValue, sortingAttribute, isSum, isMean, valueAttribute ) {
                let section = new BarSection( x, y, sectionValue, isSum, isMean  );
                if( valueAttribute ) {
                    section.valueAttributeID = valueAttribute.id;
                }
                if( sortingAttribute ) {
                    section.categoryAttributeID = sortingAttribute.id;
                    //section.categoryIndex = sortingAttribute.stats.categories.indexOf( sectionValue );
                }
                return section;
            }
            function updateSectionFromBar( section, rect, caseValue, start, valueAttribute ) {
                section.caseCount++;
                section.valueSum += caseValue || 0;
                section.rect.union( rect );
                if( section.isMean ) {
                    if( valueAxis.isHorizontal ) {
                        section.rect.x = start.x0;
                        section.rect.width = start.x - section.rect.x;
                    } else {
                        section.rect.y = start.y0;
                        section.rect.height = start.y - section.rect.y;
                    }
                    Dyn.mathUtil.normalizeRect( section.rect );
                    //console.log( section.rect );
                }
                if( valueAttribute && section.valueAttributeID !== valueAttribute.id ) {
                    section.valueAttributeID = undefined;
                    section.isMultiAttributeSum = true;
                }
            }
        }

        isNumericNonNegative( v ) {
            // NOTE: we want to detect negative values because they create misleading stack
            // plots.  Negative values would cause stacks to overlap.  We include values
            // of zero, to avoid excessive exclusion of cases (github issue 75).
            return( Dyn.mathUtil.isFinite( v ) && Dyn.mathUtil.isNonNegative( v ));
        }

        /**
         * Do we allow counting/summing/percent of sections for this bar chart?
         * Used by other plot elements to determine what bar chart is doing.
         * @param {'count'|'sum'|'%'} type -- type of statistic
         * @returns {boolean}
         */
        allowBarSectionCounts( type ) {
            Dyn.assert( [ 'count', 'sum', '%' ].includes( type ) );
            let isDisabledSumOrPercentOfMeans = this.barOfMeans && (type === 'sum' || type === '%');
            return this.barsStacked && // bars must be stacked to make sections
                !(this.barsIncomplete ||   // bar chart must be complete (have necessary attributes)
                    isDisabledSumOrPercentOfMeans);   // we don't allow total or % of means (does not make sense)
        }

        /** do we allow value labels on bars? */
        allowBarValueLabels() {
            // must be a value bar chart, and not incomplete
            return this[Dyn.EPlotControl.kControlsBarChartOfValues] && !this.barsIncomplete;
        }

        displayOrderBy() {
            return ! this.barsStacked; // display the order by value always
        }

        allowCustomOrderBy() {
            return ! this.barsSectioned; // true unless we have a stacked bar with sections that need a particular order.
        }

        /**
         * Is the bar length based on a numeric value attribute?
         * Used by other plot elements to determine what bar chart is doing.
         * Note that this returns FALSE for a ValueBarElement with no attributes on the value axis.
         * @returns {boolean}
         */
        isValueBarChart() {
            return( this.onX.hasValueAttribute || this.onY.hasValueAttribute );
        }

        /** does this bar chart have a frequency axis? */
        isFrequencyBarChart() {
            return( this.onX.isFrequencyAxis || this.onY.isFrequencyAxis );
        }

        /**
         * Get any extra tool tip text for the given case.
         * Here the bar chart adds extra context for count and percent
         * only if this is an aggregate pie chart.
         * @param {number} caseIndex
         * @returns {string}
         */
        getExtraTooltipForCase( caseIndex, cellIndex, sectionIndex ) {
            Dyn.assert( this.plotShaper.bins.cells.length > 0, "must populate cells first");
            let plotShaper = this.plotShaper,
                sectionText = '',
                hasCountOrPercent =
                    plotShaper.hasElement( Dyn.EPlotControl.kControlsSectionCount ) ||
                    plotShaper.hasElement( Dyn.EPlotControl.kControlsSectionPercentage );

            if( hasCountOrPercent && cellIndex >= 0 && sectionIndex >= 0 ) {
                let cell = plotShaper.bins.cells[cellIndex];
                let section = cell && cell.barSections && cell.barSections[sectionIndex];
                if( section && section.countPercentTip ) {
                    sectionText = section.countPercentTip;
                }
            }
            return sectionText;
        }

        /**
         * Alows each element to respond to an axis button click
         * @param ioWhatChanged -- element should add a Dyn.EPlotChange parameter if returning true.
         * @param subViewRef
         * @param axisData
         * @returns {boolean} -- true if handled by a Dyn.PlotElement
         */
        handleAxisButtonClick( ioWhatChanged, subViewRef, axisData ) {
            var handled = false,
                isPlusButton  = (axisData.type==="plusButton"),
                isMinusButton = (axisData.type==="minusButton"),
                isOnLegend = (axisData.isLegend===true),
                //sortingAttribute = this.plotShaper.dataSet.getOrderByAttribute(),
                coloringAttribute = this.plotShaper.dataSet.getSelectedAttribute();

            if( isOnLegend && ( isPlusButton || isMinusButton ) && coloringAttribute && coloringAttribute.hasNumericIntervals() ) {
                // update the number of intervals; new intervals will be calculated later by calcPlotSpecificRange()
                Dyn.plotUtil.setNumIntervalsPlusMinus( coloringAttribute.numericIntervals, isPlusButton );
                ioWhatChanged[ Dyn.EPlotChange.kChAxisSize ] = true; // intervals affect axis range which affects axis view size.
                ioWhatChanged[ Dyn.EPlotChange.kChIconPosition ] = true; // icons must be positioned in new intervals
                handled = true;
                //console.log( "Bar numIntervals set to "+coloringAttribute.numericIntervals.numIntervals );
                // track analytics on histogram bin update
                _gaq.push(['_trackEvent', 'Plot Function', 'Click', "Selected Attribute numeric intervals updated"]);
            }
            return handled;
        }

        updateTitleForValueSumMeanCharts( ioWhatChanged, subViewRef ) {
            // update default titles once on plot creation, then let user customize (don't change on recalc)
            let needsValueAttribute = !Boolean( this.getValueAxis( subViewRef.xAxisView, subViewRef.yAxisView )),
                wantValueAttributeOnX = this.barsHorizontal,
                valueAxis = wantValueAttributeOnX ? subViewRef.xAxisView : subViewRef.yAxisView,
                nonValueAxis = (!wantValueAttributeOnX) ? subViewRef.xAxisView : subViewRef.yAxisView,
                isIncompleteChart = needsValueAttribute;

            this.plotShaper.setDefaultPlotTitle( subViewRef.topMargin, Dyn.loc( this.kDefaultTitle ));

            // add axis prompts if incomplete plot
            if( valueAxis.isEmptyOfAttributes() ) {
                valueAxis.setTitle( Dyn.loc( Dyn.plotStrings.promptValue ), isIncompleteChart);
            }
            if( nonValueAxis.isEmptyOfAttributes() && isIncompleteChart ) {
                nonValueAxis.setTitle( Dyn.loc( Dyn.plotStrings.promptGroup ), false );
            }

            // update axis styling
            this.onX.isIncompleteAxisStyle = valueAxis.isHorizontal ? isIncompleteChart : false; //valueAxis.setIncompleteAxis( isIncompleteChart );
            this.onY.isIncompleteAxisStyle = valueAxis.isHorizontal ? false : isIncompleteChart; //nonValueAxis.setIncompleteAxis(false );
            this.onLegend.isIncompleteAxisStyle = false; //subViewRef.rightMargin.setIncompleteAxis(false);
        }

        /**
         * Should bar orientation be saved in the plot settings? Only needed for bar charts that work with no attributes.
         * @returns {boolean}
         */
        saveBarOrientation() {
            return false; // override default behavior to always save bar orientation
        }
    } // class BarElement
    Dyn.BarElement = BarElement;

    /**
     * A BarSection holds data for a group of cases of equal value within a bar.
     * Visually, the first case in the section covers the area for that section
     * of the bar (with 1+ sections per bar) and the remaining cases in the section are hidden.
     */
    class BarSection extends Dyn.PlotSection {
        constructor( x, y, category, isSum, isMean ) {
            super( category, isSum );
            this.rect = new Dyn.mathUtil.Rect( x, y, 0, 0 );
            this.isMean = isMean;
        }
        static paramName = 'barSections';
    }
    Dyn.PlotSection.registerSectionName( BarSection.paramName );

    /** A BarValue holds the numeric value and bar position on value bars, for CountElement */
    class BarValue {
        constructor( value, rect ) {
            this.value = value;
            this.rect = rect;
        }
    }

    class BarData {
        constructor( caseCount, stackHorizontal, isReverseBar ) {
            this.caseCount = caseCount;
            this.stackHorizontal = stackHorizontal;
            this.isReverseBar = isReverseBar;
        }
        setType( isStacked, isSum, isMean, isStretched, isValueBar ) {
            this.isStacked = isStacked;
            this.isSum = isStacked && isSum;
            this.isMean = isStacked && isMean;
            this.isStretched = isStacked && isStretched;
            this.isValueBar = isValueBar;
        }
        setValueSum( valueSum ) {
            this.valueSum = valueSum;
        }
        setValueAttributeID( valueAxis ) {
            if( valueAxis && valueAxis.allAttributes.countActive() > 0 ) {
                this.valueAttributeID = valueAxis.allAttributes.attributes[0].id; // attribute for formatting valus/sum of values
            }
        }
        setBarEnd( barEnd ) {
            this.barEnd = barEnd;
        }
    }

    class BarStyle {
        constructor( cell, barsAreHorizontal, barsAreStacked, maxCasesInCells, marginForSum ) {
            // constants
            this.kCellSpace = 0.9;	// unstacked (spread) bars use this proportion of available space between cells
            this.kBarLengthUnstacked = 0.5; // unstacked, no-attribute value bars use this proportion of available space in cell
            this.kBarLengthStacked = 0.75; 	 // stacked, no-attribute value bars use this proportion of available space in cell
            this.kBarWidth = 0.8;	// unstacked (spread) bars use this proportion of available width between bars
            this.kBarWidthSingle = 0.5; // if only one bar (stacked or one case per cell), use this proportion of available width between bars
            this.kMinBarLengthPixels = 0.1; // bars are at least one pixel tall to make zero-length bars appear as a line
            this.kMinBarWidthPixels = 0.1; // bars are at least one pixel wide to be visible in the cell, even if overlapping
            this.kCellMargin = 2;	// pixels

            // variables
            this.barsHorizontal = barsAreHorizontal;
            this.cellMaxLength = Math.max(0, this.kCellSpace * cell.widthOrHeight( this.barsHorizontal ) - marginForSum);
            this.cellMaxWidth = this.kCellSpace * cell.widthOrHeight( ! this.barsHorizontal );
            this.numCasesInCell = cell.cases.length;
            this.numCasesWide = barsAreStacked ? 1 : this.numCasesInCell;
            this.maxCasesInCells = maxCasesInCells;
        }
        getBarStartPoint( barsHorizontal, cell, numCasesWide, barWidth, barWidthMargin, numericAxis ) {
            let barStartX = cell.centerX(),
                barStartY = cell.centerY();
            if ( barsHorizontal ) {
                barStartY += (0.5 * ((numCasesWide) * barWidth)) + barWidthMargin - barWidth; // offset to find center of first bar
                if (numericAxis) {
                    barStartX = numericAxis.dataToPixel(0); 	// value/freq/percent bars start at data value 0 on numeric axis
                } else {
                    barStartX = cell.left() + this.kCellMargin;		// uniform bars start at left edge of cell
                }
            } else {
                barStartX -= (0.5 * ((numCasesWide) * barWidth)) - barWidthMargin; // offset to find center of first bar
                if (numericAxis) {
                    barStartY = numericAxis.dataToPixel(0); 	// value bars start at data value 0 on numeric axis
                } else {
                    barStartY = cell.bottom() - this.kCellMargin;	// uniform bars start at bottom edge of cell
                }
            }
            this.start = { x:barStartX, y:barStartY, x0:barStartX, y0:barStartY };
        }
    } // class BarStyle


    /**
     * Creates bar charts of stacked case frequencies.
     * Any numeric attribute on the X or Y axis is ignored/disabled.
     * Variations:
     * --Bars are horizontal if one categorical attribute on Y, otherwise vertical.
     * --Chart has a single bar if no categorical attributes on X or Y.
     * --Chart has multiple bars if there is a categorical attribute on X or Y.
     * --Chart is split two ways if categorical attributes on both X and Y.
     * --Bars have colored sections if a categorical attribute on the legend,
     *      or a numeric attribute on the legend (which is split into numeric intervals)
     *      or if no categories, one section for each of multiple attributes (on X or Y).
     * @constructor
     */
    class FreqBarElement extends BarElement{

        constructor() {
            super( true /*wantBarsStacked*/ );
            this.initElement( {
                priority: Dyn.EPlotPriority.kPriorityShape,
                replaceType: Dyn.EPlotControl.kControlsShape,
                primaryType: Dyn.EPlotControl.kControlsBarChartOfFreq,
                otherControls: [
                    Dyn.EPlotControl.kControlsBarChart,
                    Dyn.EPlotControl.kControlsCaseOrder ]
            }, Dyn );
            this.needsXYPrompt = true; // set all prompts a for newly created plot only

            // constants for this type of bar chart
            this.barsByFrequencyOnly = true;
            Dyn.assert( this.barsStacked === true );
            Dyn.assert( this.barsStretched === false );
            this.multipleNumericAttributesOK = false;
            Dyn.assert( this.negativeCaseValuesOK ); // make sure we include all cases, including cases with negative values
        }

        get kDefaultTitle() { return Dyn.plotTitles.BAR_CHART_OF_FREQUENCIES; }
        get kUndoAction(){ return  'Bar Chart of Frequencies'; }

        // override of .restore() not necessary, we use registerOldType( Dyn.EPlotControl.kControlsOldUnstackedBarChart ) instead
        // Dyn.FreqBarElement.prototype.restore = function( savedData ) {
        //     this.parent.restore.call( this, savedData );
        //     if( savedData.primaryType === Dyn.EPlotControl.kControlsOldUnstackedBarChart ) {
        //         // e.g. if it has a numeric attribute on the axis, we should make a ValueBarElement instead
        //     } else {
        //         Dyn.assert( savedData.primaryType === Dyn.EPlotControl.kControlsBarChartOfFreq );
        //     }
        // };

        updatePlotTitleAndAxisStyle( ioWhatChanged, subViewRef ) {
            let groupOnX = ! this.barsHorizontal,
                groupAxis = groupOnX ? subViewRef.xAxisView : subViewRef.yAxisView,
                freqAxis = (!groupOnX) ? subViewRef.xAxisView : subViewRef.yAxisView;

            // update default titles once on plot creation, then let user customize (don't change on recalc)
            this.plotShaper.setDefaultPlotTitle( subViewRef.topMargin, Dyn.loc( this.kDefaultTitle ));

            // add or update potential group axis prompt if not yet a frequency plot (e.g. first calculation)
            if( this.needsXYPrompt ) { // set up prompts once on construction, not on recalc or restore)
                if( groupAxis.isEmptyOfAttributes() ) {
                    groupAxis.setTitle( Dyn.loc( Dyn.plotStrings.promptGroup ) );
                }
                // clear prompt on potential frequency axis, if any (keep disabled attributes as disabled)
                if( freqAxis.isEmptyOfAttributes()) {
                    freqAxis.setTitle( "", false );
                }
            }
            this.needsXYPrompt = false; // set up prompts once on construction, not on recalc or restore

            // update axis styling
            this.onX.isIncompleteAxisStyle = false;
            this.onY.isIncompleteAxisStyle = false;
            this.onLegend.isIncompleteAxisStyle = false;
        }

        /**
         * {OVERRIDE} to turn OFF Percent by Bar Section when switching to a Bar Chart of Frequencies.
         */
        replaces( iExistingPlotElement ) {
            let controlsToTurnOff = [ Dyn.EPlotControl.kControlsSectionPercentage ];
            if( this.turnOffToggleControl( iExistingPlotElement,controlsToTurnOff )) {
                return true; // remove iExistingPlotElement, it has nothing to show
            }
            // else use default behavior
            return super.replaces( iExistingPlotElement );
        }

        /** {OVERRIDE} to save bar orientation for frequency bar charts, so swapAxis() works even with no attributes on X or Y. */
        saveBarOrientation() {
            return true;
        }
    } // class FreqBarElement
    Dyn.FreqBarElement = FreqBarElement;

    /**
     * Creates bar charts where length is proportional to numeric value on one axis,
     *  one bar per non-missing case value (multiple bars if multiple numeric attributes).
     * If no numeric attribute yet, prompts user to add one.
     * These Value Bars are not stacked or stretched.
     * Variations:
     * --Bars are vertical with the numeric attribute on Y, horizontal if the numeric attribute is on X.
     * --Chart has a single bar if the non-numeric axis is empty.
     * --Chart has multiple bars if the non-numeric axis has a categorical attribute.
     * --Bars have colored sections if a categorical attribute on the legend,
     *      or a numeric attribute on the legend (which is split into numeric intervals)
     *      or if no categories, one section for each of multiple attributes (on X or Y).
     * @constructor
     */
    class ValueBarElement extends BarElement{
        constructor() {
            super( false );
            this.initElement( {
                priority: Dyn.EPlotPriority.kPriorityShape,
                replaceType: Dyn.EPlotControl.kControlsShape,
                primaryType: Dyn.EPlotControl.kControlsBarChartOfValues,
                otherControls: [
                    Dyn.EPlotControl.kControlsBarChart,
                    Dyn.EPlotControl.kControlsCaseOrder ]
            }, Dyn );

            // constants for this type of bar chart
            Dyn.assert( this.barsByFrequencyOnly === false );
            Dyn.assert( this.barsStacked === false );
            Dyn.assert( this.barsStretched === false );
            this.multipleNumericAttributesOK = true;
            Dyn.assert( this.negativeCaseValuesOK ); // make sure we include all cases, including cases with negative values
        }

        get kDefaultTitle(){ return  Dyn.plotTitles.BAR_CHART_OF_VALUES; }
        get kUndoAction(){ return 'Bar Chart of Values'; }

        updatePlotTitleAndAxisStyle( ioWhatChanged, subViewRef ) {
            this.updateTitleForValueSumMeanCharts( ioWhatChanged, subViewRef );
        }
    } // class ValueBarElement
    Dyn.ValueBarElement = ValueBarElement;

    /**
     * Creates bar charts that are always stacked, require 1+ numeric attributes on one axis (X or Y),
     * and sum the values of the numerical attribute(s) cases in the bars.
     * Variations:
     * --Bars are vertical with the numeric attribute on Y, horizontal if the numeric attribute is on X.
     * --Chart has a single bar if the non-numeric axis is empty.
     * --Chart has multiple bars if the non-numeric axis has a categorical attribute.
     * --Bars have colored sections if a categorical attribute on the legend,
     *      or a numeric attribute on the legend (which is split into numeric intervals)
     *      or if no categories, one section for each of multiple attributes (on X or Y).
     * @constructor
     */
    class BarOfSumsElement extends BarElement{
        constructor() {
            super( true /*wantBarsStacked*/ );
            this.initElement( {
                priority: Dyn.EPlotPriority.kPriorityShape,
                replaceType: Dyn.EPlotControl.kControlsShape,
                primaryType: Dyn.EPlotControl.kControlsBarChartOfSums,
                otherControls: [
                    Dyn.EPlotControl.kControlsBarChart,
                    Dyn.EPlotControl.kControlsCaseOrder ]
            }, Dyn );

            // constants for this type of bar chart
            Dyn.assert( this.barsByFrequencyOnly === false );
            Dyn.assert( this.barsStacked === true );
            Dyn.assert( this.barsStretched === false );
            // NOTE: multiple numeric attributes, if turned on, do not create a section, and are confusing with
                    // a color by legend.  For this reason we turn them off.  Otherwise the plot is fine Craig 2020-04-30
            this.multipleNumericAttributesOK = false;
            this.negativeCaseValuesOK = false; // showing % of stacked negative sections is a problem; disallow them (e.g. sum of profit-by-state == negative value)
        }

        get kDefaultTitle(){ return Dyn.plotTitles.BAR_CHART_OF_SUMS; }
        get kUndoAction() { return Dyn.plotTitles.BAR_CHART_OF_SUMS; }

        updatePlotTitleAndAxisStyle( ioWhatChanged, subViewRef ) {
            this.updateTitleForValueSumMeanCharts( ioWhatChanged, subViewRef );
        }

        /**
         * {OVERRIDE} to turn OFF Percent by Bar Section when switching to a Bar Chart of Sums.
         */
        replaces( iExistingPlotElement ) {
            let controlsToTurnOff = [ Dyn.EPlotControl.kControlsSectionPercentage ];
            if( this.turnOffToggleControl( iExistingPlotElement,controlsToTurnOff )) {
                return true; // remove iExistingPlotElement, it has nothing to show
            }
            // else use default behavior
            return super.replaces( iExistingPlotElement );
        }
    } // class BarOfSumsElement
    Dyn.BarOfSumsElement = BarOfSumsElement;

    /**
     * Creates bar charts that are always stacked, require 1+ numeric attributes on one axis (X or Y),
     * and show the mean of the numerical attribute case values in the bars.
     * Variations:
     * --Bars are vertical with the numeric attribute on Y, horizontal if the numeric attribute is on X.
     * --Chart has a single bar if the non-numeric axis is empty.
     * --Chart has multiple bars if the non-numeric axis has a categorical attribute.
     * --Bars have colored sections if a categorical attribute on the legend,
     *      or a numeric attribute on the legend (which is split into numeric intervals)
     *      or if no categories, one section for each of multiple attributes (on X or Y).
     * @constructor
     */
    class BarOfMeansElement extends BarElement {
        constructor() {
            super( true /*wantBarsStacked*/ );
            this.initElement( {
                priority: Dyn.EPlotPriority.kPriorityShape,
                replaceType: Dyn.EPlotControl.kControlsShape,
                primaryType: Dyn.EPlotControl.kControlsBarChartOfMeans,
                otherControls: [
                    Dyn.EPlotControl.kControlsBarChart,
                    Dyn.EPlotControl.kControlsCaseOrder ]
            }, Dyn );

            // constants for this type of bar chart
            Dyn.assert( this.barsByFrequencyOnly === false );
            Dyn.assert( this.barsStacked === true );
            this.barOfMeans = true;
            Dyn.assert( this.barsStretched === false );
            this.multipleNumericAttributesOK = false;
            Dyn.assert( this.negativeCaseValuesOK ); // make sure we include all cases, including cases with negative values
        }

        get kDefaultTitle() { return Dyn.plotTitles.BAR_CHART_OF_MEANS; }
        get kUndoAction() { return Dyn.plotTitles.BAR_CHART_OF_MEANS; }

        updatePlotTitleAndAxisStyle( ioWhatChanged, subViewRef ) {
            this.updateTitleForValueSumMeanCharts( ioWhatChanged, subViewRef );
        }
        /**
         * {OVERRIDE} to turn OFF Percent by Bar Section, Section Sum, when switching to a Bar Chart of Means.
         */
        replaces( iExistingPlotElement ) {
            let controlsToTurnOff = [ Dyn.EPlotControl.kControlsSectionPercentage, Dyn.EPlotControl.kControlsSectionSum ];
            if( this.turnOffToggleControl( iExistingPlotElement,controlsToTurnOff )) {
                return true; // remove iExistingPlotElement, it has nothing to show
            }
            // else use default behavior
            return super.replaces( iExistingPlotElement );
        }
    } // class BarOfMeansElement
    Dyn.BarOfMeansElement = BarOfMeansElement ;

    /**
     * Creates bar charts that stacked and use a percentage (relative) scale so that
     * the stack in each bin of the plot is stretched to 100%.
     * Variations:
     * --Bars are Stretched Sums of Value if 1+ numeric attributes are on one axis
     * --Bars are Stretched Frequencies (# of cases) if no numeric attribute
     * --Bars are horizontal if only a numeric attribute on X, or only a categorical attribute on Y.
     * --Multiple bars appear if split by category on one axis (X or Y).
     * --Bars have colored sections if a categorical attribute on the legend,
     *      or a numeric attribute on the legend (which is split into numeric intervals)
     *      or if multiple attributes and no categories, one section for each attribute (on X or Y).
     * @constructor
     */
    class StretchedBarElement extends BarElement {
        constructor() {
            super( true /*wantBarsStacked*/ );
            this.initElement( {
                priority: Dyn.EPlotPriority.kPriorityShape,
                replaceType: Dyn.EPlotControl.kControlsShape,
                primaryType: Dyn.EPlotControl.kControlsStretchedBarChart,
                otherControls: [
                    Dyn.EPlotControl.kControlsBarChart,
                    Dyn.EPlotControl.kControlsCaseOrder ]
            }, Dyn );
            this.needsXYPrompt = true; // set all prompts a for newly created plot only

            // constants for this type of bar chart
            Dyn.assert( this.barsByFrequencyOnly === false );
            Dyn.assert( this.barsStacked === true );	 // barsStacked always true for stretched bars
            this.barsStretched = true;   // barsStretched === stretch stacked bars to the same total length
            // NOTE: multiple numeric attributes, if turned on, do not create a section, and are confusing with
            // a color by legend.  For this reason we turn them off; otherwise the plot is fine -Craig 2020-04-30
            this.multipleNumericAttributesOK = false;
            this.negativeCaseValuesOK = false; // showing % of stacked negative sections is a problem; disallow them (e.g. sum of profit-by-state == negative value)
        }

        get kDefaultTitle() { return Dyn.plotTitles.STRETCHED_BAR_CHART; }
        get kUndoAction() { return Dyn.plotTitles.STRETCHED_BAR_CHART; }

        updatePlotTitleAndAxisStyle( ioWhatChanged, subViewRef ) {
            // a complete stretched bar chart must have multiple bars (split by category on X or Y) AND
            // have multiple sections to color by (categories on the legend)
            let //needsColorAxis = ! subViewRef.rightMargin.isColorAxis(),
                needsCategoricalAxis = ! (subViewRef.xAxisView.isCategoricalActiveOrInactive() || subViewRef.yAxisView.isCategoricalActiveOrInactive()),
                categoryAxis = this.barsHorizontal ? subViewRef.yAxisView : subViewRef.xAxisView,
                percentAxis = this.barsHorizontal ? subViewRef.xAxisView : subViewRef.yAxisView;
            // update default title once on plot creation, then let user customize (don't change on recalc)
            this.plotShaper.setDefaultPlotTitle( subViewRef.topMargin, Dyn.loc( this.kDefaultTitle ));

            // add axis prompts if incomplete plot
            if( this.needsXYPrompt ) { // set all prompts a for newly created plot only )
                if( categoryAxis.isEmptyOfAttributes() ) {
                    // if percent axis not yet set up, also prompt for group on category axis, otherwise leave alone.
                    categoryAxis.setTitle(Dyn.loc(Dyn.plotStrings.promptGroup));
                }
                if( percentAxis.isEmptyOfAttributes() ) {
                    percentAxis.setTitle( "" );
                }
            }
            this.needsXYPrompt = false; // set up prompts once on construction, not on recalc or restore

            // update axis styling - Stretched Bar Charts don't require any attributes on the axis
            this.onX.isIncompleteAxisStyle = false;
            this.onY.isIncompleteAxisStyle = false;
            this.onLegend.isIncompleteAxisStyle = false;
        }

        /**
         * {OVERRIDE} to turn OFF certain count and percent settings when switching to a Stretched Bar Chart.
         * See also ToolbarComponent.extraTypeDict[] for counts/percents that are turned ON.
         */
        replaces( iExistingPlotElement ) {
            let controlsToTurnOff = [ Dyn.EPlotControl.kControlsSectionSum, Dyn.EPlotControl.kControlsSectionPercentage ];
            if( this.turnOffToggleControl( iExistingPlotElement,controlsToTurnOff )) {
                return true; // remove iExistingPlotElement, it has nothing to show
            }
            // else use default behavior
            return super.replaces( iExistingPlotElement );
        }

        /** {OVERRIDE} to save bar orientation for stretched bar charts, so swapAxis() works even with no attributes on X or Y. */
        saveBarOrientation() {
            return true;
        }
    }
    Dyn.StretchedBarElement = StretchedBarElement;

    Dyn.PlotElemHelper.registerOldType( Dyn.EPlotControl.kControlsBarChart, (savedData) => { return Dyn.ValueBarElement; } );
    Dyn.PlotElemHelper.registerOldType( Dyn.EPlotControl.kControlsOldUnstackedBarChart,
        // restore the old Unstacked Bar Chart to either Bar Chart of Frequencies or Bar Chart of Values.
        function( savedData, partialPlotState ) {
            let hasNumericAttribute = partialPlotState && (
                (partialPlotState.xAxisRef && partialPlotState.xAxisRef.isNumericAxis()) ||
                (partialPlotState.yAxisRef && partialPlotState.yAxisRef.isNumericAxis())
            );
            return( hasNumericAttribute ?
                Dyn.ValueBarElement : // has a numeric attribute: convert to a Bar Chart of Values
                Dyn.FreqBarElement );   // else convert to a Bar Chart of Frequencies (now it will be stacked).
        });
    Dyn.PlotElemHelper.registerOldType( Dyn.EPlotControl.kControlsOldStackedBarChart,
        // restore the old Stacked Bar Chart to either Bar Chart of Frequencies or Bar Chart of Sums.
        function( savedData, partialPlotState ) {
            let hasNumericAttribute = partialPlotState && (
                (partialPlotState.xAxisRef && partialPlotState.xAxisRef.isNumericAxis()) ||
                (partialPlotState.yAxisRef && partialPlotState.yAxisRef.isNumericAxis())
            );
            return( hasNumericAttribute ?
                Dyn.ValueBarElement : // has a numeric attribute: convert to a Bar Chart of Values
                Dyn.BarOfSumsElement ); // has a numeric attribute: convert to a Bar Chart of Sums
        });

    Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsBarChartOfFreq, Dyn.FreqBarElement );
    Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsBarChartOfValues, Dyn.ValueBarElement );
    Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsBarChartOfSums, Dyn.BarOfSumsElement );
    Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsBarChartOfMeans, Dyn.BarOfMeansElement );
    Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsStretchedBarChart, Dyn.StretchedBarElement );
}; // wrapDynBarElement
